import { memo, useContext, useEffect, useMemo, useState } from 'react';

import { Chip, SectionForm, Text, useTheme } from '@Sage/hoi-poi-ui';
import { dot, isEmptyObject } from '@Sage/ssm-utils';
import { Position } from '@xyflow/react';
import classnames from 'classnames';

import { getAutomationVariables } from 'services/AutomationsService';
import { getLiteral } from 'utils/getLiteral';
import { errorToast } from 'utils/toast';

import { getLiteralWithParameters } from '../../../../../../../utils/getLiteral';
import {
    findNodeByType,
    CONDITION_BOOL_LOGIC_LOCALES,
    CONDITION_OPERATOR_LOCALES,
    buildPathTree,
    CONDITION_TYPE_NO_INPUT,
} from '../../../../utils';
import { AutomationBuilderCanvasContext } from '../../AutomationBuilderCanvasContext';
import AutomationBuilderBox from '../../components/AutomationBuilderBox';
import AutomationCanvasHandle from '../../components/AutomationCanvasHandle';
import ConditionBoxValue from './ConditionBoxValue';

const AutomationBuilderConditionBox = memo((props) => {
    const { data } = useContext(AutomationBuilderCanvasContext);
    const theme = useTheme();
    const nodeData = useMemo(() => findNodeByType(data?.scene, props.id), [data?.scene, props.id]);
    const [entityFieldsMap, setEntityFieldsMap] = useState();

    const hasData = useMemo(() => {
        return (
            !isEmptyObject(nodeData?.parameters?.conditions) &&
            !nodeData?.parameters?.conditions?.parameters?.variable &&
            nodeData?.parameters?.conditions?.parameters?.length > 0 &&
            entityFieldsMap
        );
    }, [entityFieldsMap, nodeData]);

    useEffect(() => {
        if (hasData) return;
        const nodePath = dot.findPathByAttr(data, 'id', props.id);
        if (!nodePath) return;
        const nodeBranch = buildPathTree(data, nodePath);
        getAutomationVariables(nodeBranch?.scene)
            .then((result) => {
                const newEntityFieldsMap = {
                    'event.data': {
                        label: ` (${getLiteralWithParameters('label_automation_entity_trigger', [
                            getLiteral(data?.scene?.parameters?.entity?.description),
                        ])})`,
                    },
                };

                result?.variables?.related?.forEach((related) => {
                    newEntityFieldsMap[related.value] = {
                        label: ` (${getLiteral(related?.description)})`,
                    };
                });

                result?.variables?.event?.data?.sections?.forEach((section) => {
                    section.fields.map((field) => {
                        newEntityFieldsMap[field.value] = {
                            ...field,
                            label: `${getLiteral(field.literal)} (${getLiteralWithParameters(
                                'label_automation_entity_trigger',
                                [getLiteral(data?.scene?.parameters?.entity?.description)],
                            )})`,
                            icon: field.tableRelated?.table,
                        };
                    });
                });

                result?.variables?.related?.forEach((entity) => {
                    entity.sections?.forEach((section) =>
                        section.fields?.forEach((field) => {
                            newEntityFieldsMap[field.value] = {
                                ...field,
                                label: `${getLiteral(field.literal)} (${getLiteral(entity.description)})`,
                                icon: field.tableRelated?.table,
                            };
                        }),
                    );
                });

                setEntityFieldsMap(newEntityFieldsMap);
            })
            .catch((e) => {
                console.error(e);
                errorToast({ text: getLiteral('error_generalerror') });
            });
    }, [data, hasData, props.id]);

    const conditions = useMemo(() => {
        if (!hasData) return null;
        const conditions = nodeData?.parameters?.conditions;
        const checkLast = (idx, arr) => idx < arr.length - 1;
        return conditions?.parameters?.map((group, index) => {
            const hasConditions = !!group?.parameters?.length;

            return (
                <>
                    <SectionForm
                        key={index}
                        className="fm-automation-builder__condition-box__groups"
                        title={`${getLiteral('label_conditions_group')} ${index + 1}`}
                        isExpandable={false}
                    >
                        {!hasConditions && (
                            <Text type="subtitle">
                                {getLiteral('action_automation_add_condition')}
                            </Text>
                        )}
                        {hasConditions && (
                            <div className="fm-automation-builder__condition-box__conditions-group">
                                {group?.parameters?.map((condition, index2) => {
                                    const operator = condition?.type;
                                    const variable = condition?.parameters?.variable;
                                    return (
                                        <>
                                            <div className="fm-automation-builder__condition-box__condition-group">
                                                <Text type="subtitle" medium>
                                                    {entityFieldsMap[variable?.value]?.label}
                                                </Text>
                                                <div className="fm-automation-builder__condition-box__row-value">
                                                    <Text color={theme.colors.textLight.secondary}>
                                                        {getLiteral(
                                                            CONDITION_OPERATOR_LOCALES[operator],
                                                        )}
                                                        {!CONDITION_TYPE_NO_INPUT.includes(
                                                            operator,
                                                        ) && ':'}
                                                    </Text>
                                                    <ConditionBoxValue
                                                        field={variable}
                                                        condition={condition}
                                                        operator={operator}
                                                        entityFieldsMap={entityFieldsMap}
                                                    />
                                                </div>
                                            </div>

                                            {checkLast(index2, group?.parameters) && (
                                                <div className="fm-automation-builder__condition-box__row-logic">
                                                    <Chip isActive isFilled>
                                                        {getLiteral(
                                                            CONDITION_BOOL_LOGIC_LOCALES[
                                                                group?.type
                                                            ],
                                                        )}
                                                    </Chip>
                                                </div>
                                            )}
                                        </>
                                    );
                                })}
                            </div>
                        )}
                    </SectionForm>
                    {checkLast(index, conditions?.parameters) && (
                        <div className="fm-automation-builder__condition-box__row-logic">
                            <Chip isActive isFilled>
                                {getLiteral(CONDITION_BOOL_LOGIC_LOCALES[conditions?.type])}
                            </Chip>
                        </div>
                    )}
                </>
            );
        });
    }, [
        entityFieldsMap,
        hasData,
        nodeData?.parameters?.conditions,
        theme.colors.textLight.secondary,
    ]);

    return (
        <>
            <AutomationCanvasHandle
                className="fm-automation-builder__handler-condition"
                type="target"
                position={Position.Top}
                id="top"
                boxId={props.id}
                isIncomplete={nodeData?._isIncomplete}
            />
            <AutomationBuilderBox
                {...props}
                key={nodeData?.id}
                className={classnames('fm-automation-builder__condition-box', {
                    'fm-automation-builder__condition-box--filled': hasData,
                })}
                title={`${getLiteral('label_condition')} ${nodeData?.position}`}
                icon="deviceHub"
                emptyPlaceholder={`${getLiteral('action_automation_add_condition')}`}
                isEnd={!nodeData?.next?.length}
                isEveryTime={hasData ? nodeData?.parameters?.everyTime || false : undefined}
                isIncomplete={nodeData?._isIncomplete}
                allowDelete
            >
                {conditions}
            </AutomationBuilderBox>
            <AutomationCanvasHandle
                className="fm-automation-builder__handler-condition"
                type="source"
                position={Position.Bottom}
                id="bottom"
                boxId={props.id}
                isIncomplete={nodeData?._isIncomplete}
            />
        </>
    );
});

export default AutomationBuilderConditionBox;
